// Tiny entry point. Resolves entity credentials at runtime (when not baked at build time),
// sets window globals, then dynamically imports the app so all module-level code
// (including entity config and api.crud.ts) runs AFTER the globals are ready.

const CONFIG_CACHE_KEY = 'fonselp_cfg_' + window.location.hostname;
const API_URL = 'https://api.fonselp.com/';

function applyConfig(data: Record<string, string>) {
  window.__FONSELP_ENTITY_ID__ = String(data.entity_id);
  window.__FONSELP_CLIENT_ID__ = String(data.client_id);
  window.__FONSELP_CLIENT_SECRET__ = data.client_secret;
  window.__FONSELP_API_URL__ = data.api_url;
}

async function fetchConfig(): Promise<Record<string, string> | null> {
  try {
    const res = await fetch(`${API_URL}api/netlify/config?domain=${window.location.hostname}`);
    if (res.ok) return res.json();
    console.error('[fonselp] Entity config not found for domain:', window.location.hostname);
  } catch (e) {
    console.error('[fonselp] Failed to load entity config:', e);
  }
  return null;
}

async function init() {
  // In local dev, these are baked string literals via Vite's envPrefix.
  // In the single production build, they resolve to undefined window globals.
  const envEntityId: string = import.meta.env.REACT_APP_ID_ENTITY || '';

  if (!envEntityId) {
    // Try localStorage cache first — render immediately, refresh in background
    const cached = localStorage.getItem(CONFIG_CACHE_KEY);
    if (cached) {
      try {
        applyConfig(JSON.parse(cached));
        const { renderApp } = await import('./bootstrap');
        renderApp();
        // Refresh cache silently for next visit
        fetchConfig().then((data) => {
          if (data) localStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify(data));
        });
        return;
      } catch {
        localStorage.removeItem(CONFIG_CACHE_KEY);
      }
    }

    // First visit: fetch, cache, then render
    const data = await fetchConfig();
    if (data) {
      applyConfig(data);
      localStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify(data));
    }
  }

  // Dynamically import the app — module-level code in the app chunk runs
  // after window globals are set, so import.meta.env.REACT_APP_* (replaced
  // by Vite define to window.__FONSELP_*) resolve correctly.
  const { renderApp } = await import('./bootstrap');
  renderApp();
}

init();
